/**
 * What the folder drill-down DOES (2026-08-20), apart from how it looks (PV14 part 2a, 2026-10-09): the breadcrumb trail
 * with per-level listings cached for the session, the fetch of the current level (retry re-arms after an error), the
 * per-level filter, owned and "shared with you" folders apart, and a toggle that hands the folder back enriched with a
 * human path from the crumbs. ./FolderBrowser.tsx draws it for the Integrations picker and today's onboarding; the new
 * design's onboarding draws the same hook. Selection stays CONTROLLED by the caller.
 */
import { useEffect, useState } from 'react';
import { browseStorageFolders, type StorageScopeFolder } from './ComposioClient';

export interface FolderCrumb {
  id: string;
  name: string;
}

export interface FolderBrowserProps {
  /** Toolkit slug of the connected storage app (e.g. 'googledrive'). */
  appSlug: string;
  /** Human-readable app name — labels the root crumb. */
  appDisplayName: string;
  /** Multi-account: browse THIS connection instead of the primary. */
  connectionId?: string;
  /** Current selection, keyed by folder id. */
  selected: Record<string, StorageScopeFolder>;
  /** Toggle a folder in/out of the selection (enriched with `path`). */
  onToggle: (folder: StorageScopeFolder) => void;
  /**
   * Optional pre-fetched ROOT listing. The onboarding sheet loads root with
   * its own retry belt before mounting the browser; seeding avoids a second
   * fetch of the same level.
   */
  initialRootListing?: StorageScopeFolder[];
}

/** Map an API error to user copy (server 403 strings pass through). */
function describeBrowseError(err: any): string {
  const msg = String(err?.message || '').trim();
  if (!msg || /^HTTP \d+$/i.test(msg)) return 'Something went wrong. Please try again.';
  return msg;
}

export interface FolderBrowserState {
  crumbs: FolderCrumb[];
  browseLoading: boolean;
  browseError: string | null;
  filter: string;
  setFilter: (value: string) => void;
  /** The filter as it is matched (trimmed, lowercased); '' when there is none. */
  filterText: string;
  /** The current level's folders the filter keeps, then split into owned and shared. */
  visibleFolders: StorageScopeFolder[];
  ownedFolders: StorageScopeFolder[];
  sharedFolders: StorageScopeFolder[];
  /** Every chosen folder, across levels. */
  selectedList: StorageScopeFolder[];
  toggleFolder: (folder: StorageScopeFolder) => void;
  enterFolder: (folder: StorageScopeFolder) => void;
  goToCrumb: (index: number) => void;
  retry: () => void;
}

export function useFolderBrowser({
  appSlug,
  appDisplayName,
  connectionId,
  selected,
  onToggle,
  initialRootListing,
}: FolderBrowserProps): FolderBrowserState {
  const [crumbs, setCrumbs] = useState<FolderCrumb[]>([{ id: 'root', name: appDisplayName }]);
  const [listings, setListings] = useState<Record<string, StorageScopeFolder[]>>(
    (): Record<string, StorageScopeFolder[]> => (initialRootListing ? { root: initialRootListing } : {}));
  const [browseLoading, setBrowseLoading] = useState(false);
  const [browseError, setBrowseError] = useState<string | null>(null);
  const [retryTick, setRetryTick] = useState(0);
  const [filter, setFilter] = useState('');

  const currentFolder = crumbs[crumbs.length - 1];
  const currentListing = listings[currentFolder.id];

  // Fetch the current level's child folders (cache-aware; retryTick re-arms after errors)
  useEffect(() => {
    if (listings[currentFolder.id]) {
      // Arriving at a CACHED level must clear transient fetch state (review
      // 2026-08-20): breadcrumbing back mid-fetch cancels the in-flight
      // request (its .finally is skipped), and a deep-level error would
      // otherwise shadow the cached listing with a no-op "Try again" —
      // either way the browser wedged on Loading/error forever.
      setBrowseLoading(false);
      setBrowseError(null);
      return;
    }
    let cancelled = false;
    setBrowseLoading(true);
    setBrowseError(null);
    browseStorageFolders(appSlug, currentFolder.id, connectionId)
      .then((folders) => {
        if (!cancelled) {
          setListings((prev) => ({ ...prev, [currentFolder.id]: folders }));
        }
      })
      .catch((err: any) => {
        if (!cancelled) setBrowseError(describeBrowseError(err));
      })
      .finally(() => {
        if (!cancelled) setBrowseLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [appSlug, currentFolder.id, listings, retryTick, connectionId]);

  const selectedList = Object.keys(selected).map((id) => selected[id]);

  const toggleFolder = (folder: StorageScopeFolder) => {
    // Build a friendly path from the breadcrumb trail (crumb 0 is the app root)
    const parentPath = crumbs.slice(1).map((c) => c.name);
    onToggle({
      id: folder.id,
      name: folder.name,
      ...(folder.shared ? { shared: true } : {}),
      path:
        folder.path ||
        (folder.shared
          ? ['Shared with me', ...parentPath, folder.name].join(' / ')
          : [...parentPath, folder.name].join(' / ')),
    });
  };

  const enterFolder = (folder: StorageScopeFolder) => {
    setCrumbs((prev) => [...prev, { id: folder.id, name: folder.name }]);
    setFilter('');
  };

  const goToCrumb = (index: number) => {
    setCrumbs((prev) => prev.slice(0, index + 1));
    setFilter('');
  };

  const filterText = filter.trim().toLowerCase();
  const visibleFolders = (currentListing || []).filter(
    (f) => !filterText || f.name.toLowerCase().includes(filterText)
  );
  // Root level lists My Drive folders AND folders shared to this account
  // (owner case 2026-08-11: the folder someone wants is often shared, not
  // owned). Rendered as one list with a labeled "shared with you" section —
  // rows behave identically (check, open, drill down).
  const ownedFolders = visibleFolders.filter((f) => !f.shared);
  const sharedFolders = visibleFolders.filter((f) => f.shared);

  return {
    crumbs,
    browseLoading,
    browseError,
    filter,
    setFilter,
    filterText,
    visibleFolders,
    ownedFolders,
    sharedFolders,
    selectedList,
    toggleFolder,
    enterFolder,
    goToCrumb,
    retry: () => setRetryTick((t) => t + 1),
  };
}
